import { useState } from 'react';
import BottomSheetMobile, {
  type SheetOption,
  SheetOptionList,
} from '@/domains/mobile-web/components/BottomSheetMobile/BottomSheetMobile';
import InputMobile from '@/domains/mobile-web/components/InputMobile/InputMobile';
import PickerFieldMobile from '@/domains/mobile-web/components/PickerFieldMobile/PickerFieldMobile';
import TextAreaMobile from '@/domains/mobile-web/components/TextAreaMobile/TextAreaMobile';
import {
  useCities,
  useDistricts,
  useProvinces,
  useSubdistricts,
} from '@/domains/mobile-web/hooks/useAddresses';
import type {
  AddressDraft,
  AddressDraftField,
  Region,
} from '@/domains/mobile-web/types/address.types';

type AreaField = 'province' | 'city' | 'district' | 'subdistrict';

const AREA_CONFIG: Record<AreaField, { title: string; placeholder: string; label: string }> = {
  province: { title: 'Pilih Provinsi', placeholder: 'Cari provinsi...', label: 'Provinsi' },
  city: {
    title: 'Pilih Kota/Kabupaten',
    placeholder: 'Cari kota/kabupaten...',
    label: 'Kota/Kabupaten',
  },
  district: { title: 'Pilih Kecamatan', placeholder: 'Cari kecamatan...', label: 'Kecamatan' },
  subdistrict: {
    title: 'Pilih Kelurahan/Desa',
    placeholder: 'Cari kelurahan/desa...',
    label: 'Kelurahan/Desa',
  },
};

// Picking a region clears the ones below it
const CLEARED_BELOW: Record<AreaField, Partial<AddressDraft>> = {
  province: { city: undefined, district: undefined, subdistrict: undefined },
  city: { district: undefined, subdistrict: undefined },
  district: { subdistrict: undefined },
  subdistrict: {},
};

const digitsOnly = (value: string) => value.replace(/[^0-9]/g, '');

interface AddressFieldsProps {
  value: AddressDraft;
  onChange: (next: AddressDraft, field: AddressDraftField) => void;
  errors?: Partial<Record<AddressDraftField, string | undefined>>;
}

/** Label, the region chain, RT/RW, street and the postal code of an address form. */
const AddressFields = ({ value, onChange, errors = {} }: AddressFieldsProps) => {
  const [activeField, setActiveField] = useState<AreaField | null>(null);
  const [search, setSearch] = useState('');
  const { province, city, district, subdistrict } = value;

  // Only the list of the open sheet is requested
  const provinces = useProvinces(activeField === 'province');
  const cities = useCities(activeField === 'city' ? province?.id : undefined);
  const districts = useDistricts(activeField === 'district' ? city?.id : undefined);
  const subdistricts = useSubdistricts(activeField === 'subdistrict' ? district?.id : undefined);
  const activeQuery =
    activeField &&
    { province: provinces, city: cities, district: districts, subdistrict: subdistricts }[
      activeField
    ];

  const active = activeField ? AREA_CONFIG[activeField] : null;
  const filteredOptions: SheetOption[] = (activeQuery?.data?.rows ?? [])
    .filter((region: Region) => region.name.toLowerCase().includes(search.trim().toLowerCase()))
    .map((region: Region) => ({ value: region.id, label: region.name }));

  const setText = (field: 'label' | 'rt' | 'rw' | 'address_line', text: string) => {
    onChange({ ...value, [field]: text }, field);
  };

  const openSheet = (field: AreaField) => {
    setSearch('');
    setActiveField(field);
  };

  const closeSheet = () => {
    setActiveField(null);
    setSearch('');
  };

  const handleSelect = (option: SheetOption) => {
    if (!activeField) return;
    const region =
      activeField === 'subdistrict'
        ? subdistricts.data?.rows.find((row) => row.id === option.value)
        : { id: option.value, name: option.label };
    onChange({ ...value, ...CLEARED_BELOW[activeField], [activeField]: region }, activeField);
    closeSheet();
  };

  return (
    <>
      <InputMobile
        label="Label Alamat"
        name="label"
        placeholder="Rumah / Kantor"
        maxLength={30}
        value={value.label}
        onChange={(e) => setText('label', e.target.value)}
        error={errors.label}
      />
      <div className="grid grid-cols-2 gap-3 items-start">
        <PickerFieldMobile
          label="Provinsi"
          name="province"
          placeholder="Pilih provinsi"
          value={province?.name}
          onClick={() => openSheet('province')}
          error={errors.province}
        />
        <PickerFieldMobile
          label="Kota/Kabupaten"
          name="city"
          placeholder="Pilih kota"
          value={city?.name}
          onClick={() => openSheet('city')}
          disabled={!province}
          error={errors.city}
        />
      </div>
      <div className="grid grid-cols-2 gap-3 items-start">
        <PickerFieldMobile
          label="Kecamatan"
          name="district"
          placeholder="Pilih kecamatan"
          value={district?.name}
          onClick={() => openSheet('district')}
          disabled={!city}
          error={errors.district}
        />
        <PickerFieldMobile
          label="Kelurahan/Desa"
          name="subdistrict"
          placeholder="Pilih kelurahan"
          value={subdistrict?.name}
          onClick={() => openSheet('subdistrict')}
          disabled={!district}
          error={errors.subdistrict}
        />
      </div>
      <div className="grid grid-cols-2 gap-3 items-start">
        <InputMobile
          label="RT"
          name="rt"
          placeholder="05"
          inputMode="numeric"
          maxLength={3}
          value={value.rt}
          onChange={(e) => setText('rt', digitsOnly(e.target.value))}
          error={errors.rt}
        />
        <InputMobile
          label="RW"
          name="rw"
          placeholder="05"
          inputMode="numeric"
          maxLength={3}
          value={value.rw}
          onChange={(e) => setText('rw', digitsOnly(e.target.value))}
          error={errors.rw}
        />
      </div>
      <TextAreaMobile
        label="Alamat Lengkap"
        name="address_line"
        placeholder="Nama jalan, nomor rumah, patokan"
        autoComplete="street-address"
        maxLength={500}
        value={value.address_line}
        onChange={(e) => setText('address_line', e.target.value)}
        error={errors.address_line}
      />
      {/* Comes with the chosen kelurahan/desa, never typed */}
      <InputMobile
        label="Kode POS"
        name="postal_code"
        placeholder="Terisi dari kelurahan/desa"
        readOnly
        value={subdistrict?.postal_code ?? ''}
      />

      <BottomSheetMobile
        open={activeField !== null}
        onClose={closeSheet}
        title={active?.title}
        searchable
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={active?.placeholder}
      >
        {activeQuery?.isLoading ? (
          <p className="body1 text-text-grey">Memuat...</p>
        ) : activeQuery?.isError ? (
          <p className="body1 text-text-grey">Gagal memuat daftar {active?.label.toLowerCase()}.</p>
        ) : (
          <SheetOptionList
            options={filteredOptions}
            value={activeField ? value[activeField]?.id : undefined}
            onSelect={handleSelect}
          />
        )}
      </BottomSheetMobile>
    </>
  );
};

export default AddressFields;
